import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getCustomQuizDeleteUrl, getCustomQuizGetByIdUrl, getCustomQuizGetUrl, getCustomQuizPostUrl, getDailyHistoryUrl, getFavoriteQuestionDeleteUrl, getFavoriteQuestionPostUrl, getFavoriteQuestionUrl, getQuizSubmissionDetailsUrl, getQuizSubmissionEndUrl, getQuizSubmissionStartUrl, getSaveAnswersUrl, getStreakUrl, getTagUrl, getPublicBcsTermsUrl, getPublicSubjectsUrl } from '../utils/UrlUtilities'; // Re-using for mock tag data
import { Input } from './ui/input';
import MathText from './ui/MathText';
import CloseButton from './ui/CloseButton';
import YearPicker from './ui/YearPicker';
import AudioPlayer from './ui/AudioPlayer';
import ClozePassage from './ui/ClozePassage';
import QuizQuestionCard from './QuizQuestionCard';
import QuizPracticeInterface from './QuizPracticeInterface';
import HighlightableText from './ui/HighlightableText';
import ResizableSplit from './ui/ResizableSplit';
import ToeflModelTest from './ToeflModelTest';
import IeltsModelTest from './IeltsModelTest';
import GreModelTest from './GreModelTest';
import SatModelTest from './SatModelTest';
import { Trash2, PlayCircle, Plus, ChevronDown, ChevronUp, Star, ClipboardListIcon, TrophyIcon, Flame } from 'lucide-react'; // Icons
import { useData } from '../contexts/DataContext';
import { useUser } from '../contexts/UserContext';
import { useError } from './ErrorContext';
import { QUIZ_VIEW_TYPES, quizViewLabel, visibleQuizViews, RETAKE_ELIGIBLE_QUIZ_TYPES, BOARD_VALUES, isBoardLevel, isLanguageLevel, BCS_LEVEL, sortBcsTerms, OPTION_KEYS, TC_BLANK_LABELS, microGroupNames } from '../utils/Constants';
import { constructQuizSubmissionEndPayload, constructQuizSubmissionStartPayload, constructSaveAnswersPayload, getCustomQuizDeletePayload, getCustomQuizPostPayload, getFavoriteQuestionPostPayload, getQuizSubmissionDetailsPayload } from '../utils/PayloadUtils';
import { buildSelectedOptionsJson, clearInProgressAnswers, constructFavoriteQuestionIdToBoolMap, loadInProgressAnswers, modifyQuizzesSubmissionDetails, saveInProgressAnswers } from '../utils/BasicUtilities';
import { formatBdPst } from '../utils/DateUtilities';
import QuizScoreLeaderboardModal from './UserQuizScoreLeaderboard';
import QuizSubmissionDetailsModal from './UserQuizSubmissionDetail';
import useLocalStorageLevel from '../contexts/levelHook';
import { QuizGrid } from './UserQuizGrid';
import { QuizAddUserForm } from './UserQuizAddForm';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import UpgradeNudge from './UpgradeNudge';
import AnsweredTracker from './ui/AnsweredTracker';
import './components.css';

const QuizDetailModal = ({ quiz, quizSubmissionDetails, onClose, onStartQuiz, onStartPractice, onRetakeQuiz }) => {
    const [quizId, setQuizId] = useState(quiz?.quiz_id);
    const [quizDetails, setQuizDetails] = useState(quiz);
    const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
    const [showScoreLeaderboard, setShowScoreLeaderboard] = useState(false);
    // Micro tests can be taken as a timed model test OR self-paced practice (reveal answers).
    const [mode, setMode] = useState('test'); // 'test' | 'practice'

    let submissionStatus = quizSubmissionDetails?.submission_status || 'no-submission-yet';
    const quizType = quiz.quiz_type;
    const isMicro = quizType === 'micro';
    const startPractice = () => (typeof onStartPractice === 'function' ? onStartPractice(quiz) : onStartQuiz(quiz));
    const CallApi = useApiCaller();

    const fetchQuiz = async () => {
        try {
            const userQuizUrl = getCustomQuizGetByIdUrl(quizId);
            console.log("Fetching quizzes from:", userQuizUrl);
            const userQuizGetByIdResponse = await CallApi({ url: userQuizUrl, method: 'GET', token: true });
            if (userQuizGetByIdResponse.success) setQuizDetails(userQuizGetByIdResponse?.data);
        } catch (err) {
            console.log(`Fetching quiz get by id error ${err}`);
            setQuizDetails(quiz);
        }
    };

    useEffect(() => { fetchQuiz(); }, []);
    // Lock background scroll while the modal is open.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);
    if (!quiz) return null;

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(1px)' }}>
            <div style={{ backgroundColor: '#ffffff', borderRadius: '0.9rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '90%', maxWidth: 'min(600px, 94vw)', height: 'min(85vh, 560px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', padding: '1.1rem 1.25rem', position: 'relative', flexShrink: 0 }}>
                    <CloseButton onClick={onClose} />
                    <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, paddingRight: '2rem' }}>{quizDetails.title}</h2>
                </div>
                <div style={{ overflowY: 'auto', padding: '1.5rem', flex: 1 }}>
                <p style={{ marginBottom: '1rem', color: '#4b5563' }}>{quizDetails.description}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', marginBottom: '1.5rem' }}>
                    <span><strong>Level:</strong> {quizDetails.level}</span>
                    {quizDetails.division ? <span><strong>Division:</strong> {quizDetails.division}</span> : null}
                    {quizDetails.unit ? <span><strong>Unit:</strong> {quizDetails.unit}</span> : null}
                    {quizDetails.board ? <span><strong>Board:</strong> {quizDetails.board}</span> : null}
                    {quizDetails.year ? <span><strong>Exam Year:</strong> {quizDetails.year}</span> : null}
                    <span><strong>Type:</strong> {quizDetails.quiz_type}</span>
                    <span><strong>Duration:</strong> {quizDetails.duration_minutes} mins</span>
                    <span><strong>Start Time:</strong> {quizDetails.start_time ? formatBdPst(quizDetails.start_time) : 'N/A'}</span>
                    {quizDetails.quiz_type === 'compitition' && (
                        <span><strong>Ends:</strong> {quizDetails.end_time ? formatBdPst(quizDetails.end_time) : 'N/A'}</span>
                    )}
                    <span><strong>Created By:</strong> {quizDetails.created_by}</span>
                    {quizDetails.question_ids && (
                        <>
                            {Object.entries(quizDetails.question_ids).map(([subject, questions]) => (
                                <span key={subject}><strong>{subject}:</strong> {questions.length} questions</span>
                            ))}
                            <span style={{ gridColumn: '1 / -1' }}><strong>Total Questions:</strong> {quizDetails.total_questions ?? Object.values(quizDetails.question_ids).reduce((sum, list) => sum + list.length, 0)}</span>
                        </>
                    )}
                </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', flexShrink: 0, backgroundColor: '#ffffff' }}>
                    {isMicro && submissionStatus !== 'in-progress' && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {[['practice', 'Practice The Questions'], ['test', 'Take Model Test']].map(([val, label]) => (
                                <button
                                    key={val}
                                    onClick={() => setMode(val)}
                                    style={{
                                        flex: 1, padding: '0.5rem', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                                        border: mode === val ? '2px solid #7c3aed' : '1px solid #d1d5db',
                                        backgroundColor: mode === val ? '#f5f3ff' : '#ffffff',
                                        color: mode === val ? '#5b21b6' : '#4b5563',
                                    }}
                                >{label}</button>
                            ))}
                        </div>
                    )}
                    {submissionStatus === 'no-submission-yet' && (
                        <button onClick={() => (isMicro && mode === 'practice' ? startPractice() : onStartQuiz(quiz))} style={{ width: '100%', backgroundColor: '#22c55e', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            <PlayCircle style={{ width: '1.25rem', height: '1.25rem' }} /> {isMicro && mode === 'practice' ? 'Start Practice' : 'Start Test'}
                        </button>
                    )}
                    {submissionStatus === 'completed' && RETAKE_ELIGIBLE_QUIZ_TYPES.includes(quizDetails.quiz_type) && (
                        <>
                            <button onClick={() => (isMicro && mode === 'practice' ? startPractice() : (typeof onRetakeQuiz === 'function' ? onRetakeQuiz(quiz) : onStartQuiz(quiz)))} style={{ width: '100%', backgroundColor: '#f97316', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <PlayCircle style={{ width: '1.25rem', height: '1.25rem' }} /> {isMicro && mode === 'practice' ? 'Start Practice' : 'Retake Test'}
                            </button>
                            <button onClick={() => setShowSubmissionDetails(true)} style={{ width: '100%', backgroundColor: '#3b82f6', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <ClipboardListIcon style={{ width: '1.25rem', height: '1.25rem' }} /> Show Submission Details
                            </button>
                            <button onClick={() => setShowScoreLeaderboard(true)} style={{ width: '100%', backgroundColor: '#6366f1', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <TrophyIcon style={{ width: '1.25rem', height: '1.25rem' }} /> Show Score & Leaderboard
                            </button>
                        </>
                    )}
                    {submissionStatus === 'in-progress' && (
                        <button onClick={() => onStartQuiz({...quiz, "submission_id": quizSubmissionDetails.submission_id, "start_time": quizSubmissionDetails.start_time, "end_time": quizSubmissionDetails.end_time}, true)} style={{ width: '100%', backgroundColor: '#f59e0b', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            <PlayCircle style={{ width: '1.25rem', height: '1.25rem' }} /> Resume Test
                        </button>
                    )}
                    {/* Competition: results (details + leaderboard) unlock only after the window closes. */}
                    {submissionStatus === 'completed' && quizDetails.quiz_type === 'compitition' && (
                        (quizDetails.end_time && new Date(quizDetails.end_time) <= new Date()) ? (
                            <>
                                <button onClick={() => setShowSubmissionDetails(true)} style={{ width: '100%', backgroundColor: '#3b82f6', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                    <ClipboardListIcon style={{ width: '1.25rem', height: '1.25rem' }} /> Show Submission Details
                                </button>
                                <button onClick={() => setShowScoreLeaderboard(true)} style={{ width: '100%', backgroundColor: '#6366f1', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                    <TrophyIcon style={{ width: '1.25rem', height: '1.25rem' }} /> Show Score & Leaderboard
                                </button>
                            </>
                        ) : (
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                                You've submitted. Results &amp; leaderboard open after the competition ends
                                {quizDetails.end_time ? <> — <strong>{formatBdPst(quizDetails.end_time)}</strong></> : null}.
                            </p>
                        )
                    )}
                </div>
            </div>

            {showSubmissionDetails && (
                <QuizSubmissionDetailsModal submissionId={quizSubmissionDetails?.submission_id} onClose={() => setShowSubmissionDetails(false)} />
            )}

            {showScoreLeaderboard && (
                <QuizScoreLeaderboardModal quizId={quizId} submissionId={quizSubmissionDetails?.submission_id} onClose={() => setShowScoreLeaderboard(false)} />
            )}
        </div>
    );
};


const QuizTakingInterface = ({ quizParam, onQuizEnd, onRetakeQuiz }) => {
    // console.log("In quiz taking interface , quiz param :: ", quizParam);
    const [quizId, setQuizId] = useState(quizParam?.quiz_id);
    const [quiz, setQuiz] = useState(quizParam);
    const [questionsBySubject, setQuestionsBySubject] = useState({});
    // Restore any answers persisted for this in-progress submission so a mid-quiz
    // reload (then Resume) keeps the user's selections and answered count.
    const [userAnswers, setUserAnswers] = useState(() => loadInProgressAnswers(quizParam?.submission_id)); // { questionId: selectedOptionKey }
    const [favoriteQuestions, setFavoriteQuestions] = useState({}); // { questionId: true/false }
    const [expandedSubjects, setExpandedSubjects] = useState({}); // { subjectName: true/false }

    const endTime = new Date(quizParam.end_time).getTime();
    const currentTime = Date.now();
    const initialTimeLeft = Math.max(Math.floor((endTime - currentTime) / 1000), 0); // in seconds
    console.log("In quiz taking interface , initialTimeLeft :: ", initialTimeLeft);
    const [timeLeft, setTimeLeft] = useState(initialTimeLeft);

    const [quizEnded, setQuizEnded] = useState(false);
    const [takeSubmitConfirmation, setTakeSubmitConfirmation] = useState(false);
    // On the post-submission "Quiz Ended" screen, let the user review their
    // submission or the leaderboard without leaving the quiz-taking flow.
    const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
    const [showScoreLeaderboard, setShowScoreLeaderboard] = useState(false);
    // Reader passage highlights, keyed per passage group, kept for the whole quiz session.
    const [highlightsByPassage, setHighlightsByPassage] = useState({});

    const CallApi = useApiCaller();

    // Calculate total questions and answered questions for display
    const totalQuestionsCount = useMemo(() => {
        return Object.values(questionsBySubject).flat().length;
    }, [questionsBySubject]);

    const answeredQuestionsCount = useMemo(() => {
        return Object.keys(userAnswers).length;
    }, [userAnswers]);

    // Persist in-progress answers on every change so they survive a reload.
    useEffect(() => {
        saveInProgressAnswers(quizParam?.submission_id, userAnswers);
    }, [userAnswers, quizParam?.submission_id]);

    // Server backstop: debounce-save answers to the server for ANY timed quiz (a quiz with an
    // end_time), so an abandoned attempt (browser closed before the countdown fires) can still be
    // auto-finalized and scored by the server's overdue sweep — even if the user never returns /
    // switches device. The in-render timer above already handles the tab-open expiry case.
    useEffect(() => {
        if (!quizParam?.submission_id || !quizParam?.end_time) return undefined;
        if (!userAnswers || Object.keys(userAnswers).length === 0) return undefined;
        const t = setTimeout(() => {
            CallApi({
                url: getSaveAnswersUrl(quizParam.submission_id),
                method: 'POST',
                payload: constructSaveAnswersPayload(userAnswers),
                token: true,
                silent: true,
            }).catch(err => console.error('save-answers failed', err));
        }, 3000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAnswers, quizParam?.submission_id, quizParam?.end_time]);

    const fetchQuizQuestions = useCallback(async () => {
        try {

            const userQuizUrl = getCustomQuizGetByIdUrl(quizId);
            console.log("Fetching quizzes from:", userQuizUrl);

            const userQuizGetByIdResponse = await CallApi({
                url: userQuizUrl,
                method: 'GET',
                token: true
            });

            if (userQuizGetByIdResponse.success) {
                const fetchedQuiz = userQuizGetByIdResponse?.data;
                setQuiz(fetchedQuiz);
            } else {
                console.log(`Error in fetching quiz details ${userQuizGetByIdResponse}`);
                setQuiz(quizParam);
            }

            const fetchedQuestions = userQuizGetByIdResponse?.data?.questions;
            const groupedQuestions = fetchedQuestions;

            setQuestionsBySubject(groupedQuestions);

            // Initialize all subjects as expanded by default
            const initialExpanded = {};
            Object.keys(groupedQuestions).forEach(subject => {
                initialExpanded[subject] = true;
            });
            setExpandedSubjects(initialExpanded);

        } catch (error) {
            console.error("Error fetching quiz questions:", error);
            setQuiz(quizParam); // Fallback to initial param if API fails
            setQuestionsBySubject({});
        }
    }, [quizId, quizParam]);

    
    const fetchFavoriteQuestions = useCallback(async () => {
        try {

            const questionUrl = getFavoriteQuestionUrl();
            const questionGetResponse = await CallApi({
                url: questionUrl,
                method: 'GET',
                token: true,
            });

            if (questionGetResponse.success) {
                let favoriteQuestionIdToBoolMap = constructFavoriteQuestionIdToBoolMap(questionGetResponse?.data?.favorites);
                setFavoriteQuestions(favoriteQuestionIdToBoolMap);
            } else {
                console.error("Error fetching favorite questions:");
            }

        } catch (error) {
            console.error("Error fetching favorite questions:", error);
        }
    }, []); // Depend on quizId and quizParam

    useEffect(() => {
        fetchFavoriteQuestions();
        fetchQuizQuestions();
    }, []); // Re-fetch questions when fetchQuizQuestions callback changes

    // Modify the timer effect:
    useEffect(() => {
        // Don't set up timer if quiz has already ended
        if (quizEnded) return;
        
        // Check if time is up
        if (timeLeft <= 0) {
            console.log("Time's up! Submitting quiz...");
            
            try {
                const answers = buildSelectedOptionsJson(userAnswers);
                console.log("User selected answers:", answers);

                onQuizEnd({
                    ...quizParam,
                    submitted_answers: answers
                }).then(() => {
                    // After all API calls are complete, show the Quiz Ended screen
                    clearInProgressAnswers(quizParam.submission_id);
                    setQuizEnded(true);
                    if (['daily', 'custom', 'micro'].includes(quiz?.quiz_type) || ['daily', 'custom', 'micro'].includes(quizParam?.quiz_type)) setShowSubmissionDetails(true);
                }).catch(error => {
                    console.error("Error in quiz submission:", error);
                    setQuizEnded(true);
                    if (['daily', 'custom', 'micro'].includes(quiz?.quiz_type) || ['daily', 'custom', 'micro'].includes(quizParam?.quiz_type)) setShowSubmissionDetails(true);
                });
            } catch (error) {
                console.error("Error ending quiz:", error);
                alert("There was an error submitting your test. Please try again.");
            }
            
            return; // Exit early to prevent setting up a new timer
        }
        
        // Set up the countdown timer
        const timerId = setInterval(() => {
            setTimeLeft(prev => prev - 1);
        }, 1000);
        
        // Clean up the timer when component unmounts or effect reruns
        return () => clearInterval(timerId);
    }, [timeLeft, quizEnded, userAnswers, quizParam, onQuizEnd]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleOptionSelect = (questionId, selectedOptionKey) => {
        setUserAnswers(prev => {
            // If the currently selected option for this question is clicked again, deselect it.
            if (prev[questionId] === selectedOptionKey) {
                const newAnswers = { ...prev };
                delete newAnswers[questionId]; // Remove the answer for deselection
                return newAnswers;
            } else {
                // Otherwise, select the new option.
                return {
                    ...prev,
                    [questionId]: selectedOptionKey
                };
            }
        });
    };

    // Multi-answer ("select all that apply"): the stored value is an array of option keys.
    const handleMultiOptionToggle = (questionId, optionKey) => {
        setUserAnswers(prev => {
            const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
            const next = current.includes(optionKey)
                ? current.filter(k => k !== optionKey)
                : [...current, optionKey];
            const newAnswers = { ...prev };
            if (next.length === 0) delete newAnswers[questionId];
            else newAnswers[questionId] = next;
            return newAnswers;
        });
    };

    // Free-text (TEXT) answers are stored as { text: "..." }.
    const handleTextAnswerChange = (questionId, text) => {
        setUserAnswers(prev => {
            const newAnswers = { ...prev };
            if (!text) delete newAnswers[questionId];
            else newAnswers[questionId] = { text };
            return newAnswers;
        });
    };

    // Text Completion: one choice per blank, stored as { blanks: { blankIdx: choiceIdx } }.
    const handleBlankSelect = (questionId, blankIdx, choiceIdx) => {
        setUserAnswers(prev => {
            const cur = (prev[questionId] && prev[questionId].blanks) || {};
            const nextBlanks = { ...cur };
            if (nextBlanks[blankIdx] === choiceIdx) delete nextBlanks[blankIdx];
            else nextBlanks[blankIdx] = choiceIdx;
            const newAnswers = { ...prev };
            if (Object.keys(nextBlanks).length === 0) delete newAnswers[questionId];
            else newAnswers[questionId] = { blanks: nextBlanks };
            return newAnswers;
        });
    };

    // Build a Sentence: ordered tile indices, stored as { order: [2, 0, 1, ...] }.
    const handleOrderChange = (questionId, order) => {
        setUserAnswers(prev => {
            const newAnswers = { ...prev };
            if (!order || order.length === 0) delete newAnswers[questionId];
            else newAnswers[questionId] = { order };
            return newAnswers;
        });
    };

    const handleToggleFavorite = async (questionId) => {
        const isFavorite = favoriteQuestions[questionId];

        if (isFavorite) {
            // Call DELETE API to remove from favorites
            try {
                const deleteUrl = getFavoriteQuestionDeleteUrl([questionId]);
                const response = await CallApi({ url: deleteUrl, method: 'DELETE', payload: null, token: true });
                if (!response || response.success) {
                    console.error("Delete Favorite Question Error:");
                }
                setFavoriteQuestions({
                    ...favoriteQuestions,
                    [questionId]: !isFavorite
                });
            } catch (error) {
                console.error("Delete Favorite Question Error:", error);
            }
        } else {
            // Call POST API to add to favorites
            try {
                const favoriteQuestionPostPayload = getFavoriteQuestionPostPayload(questionId);
                const favoriteQuestionPostUrl = getFavoriteQuestionPostUrl();
                const response = await CallApi({
                    url: favoriteQuestionPostUrl,
                    method: 'POST',
                    payload: favoriteQuestionPostPayload,
                    token: true,
                });
                if (!response || response.success) {
                    console.error("Adding Favorite Question Error:");
                }
                setFavoriteQuestions({
                    ...favoriteQuestions,
                    [questionId]: !isFavorite
                });
            } catch (error) {
                console.error('Failed to add favorite:', error);
            }
        }
    };

    const handleToggleSubjectCollapse = (subjectName) => {
        setExpandedSubjects(prev => ({
            ...prev,
            [subjectName]: !prev[subjectName]
        }));
    };

    // A finished daily challenge jumps straight to its submission review (rendered from the
    // Quiz Ended screen), so the user sees their answers presented like the quiz-taking modal.
    const isDailyQuiz = () => (quiz?.quiz_type === 'daily' || quizParam?.quiz_type === 'daily');

    const handleSubmitQuiz = () => {
        // setTakeSubmitConfirmation(true);
        console.log("User Answers:", userAnswers);
        const answers = buildSelectedOptionsJson(userAnswers)
        console.log("user selected answers:", answers);
        onQuizEnd({
            ...quizParam,
            submitted_answers: answers
        }).then(() => {
            // After all API calls are complete, show the Quiz Ended screen
            clearInProgressAnswers(quizParam.submission_id);
            setQuizEnded(true);
            if (isDailyQuiz() || quiz?.quiz_type === 'custom' || quizParam?.quiz_type === 'custom' || quiz?.quiz_type === 'micro' || quizParam?.quiz_type === 'micro') setShowSubmissionDetails(true);
        }).catch(error => {
            console.error("Error in quiz submission:", error);
            setQuizEnded(true);
            if (isDailyQuiz() || quiz?.quiz_type === 'custom' || quizParam?.quiz_type === 'custom' || quiz?.quiz_type === 'micro' || quizParam?.quiz_type === 'micro') setShowSubmissionDetails(true);
        });
    };

    if (quizEnded) {
        const totalQuestions = quiz?.question_ids
            ? Object.values(quiz.question_ids).reduce((sum, list) => sum + list.length, 0)
            : totalQuestionsCount;
        const canRetake = RETAKE_ELIGIBLE_QUIZ_TYPES.includes(quiz?.quiz_type);
        return (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '0.9rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '90%', maxWidth: 'min(600px, 94vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', margin: 'auto' }}>
            <div style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', padding: '1.5rem 1.25rem', textAlign: 'center', flexShrink: 0, position: 'relative' }}>
              <CloseButton onClick={() => { setTakeSubmitConfirmation(false); setQuizEnded(false); onQuizEnd(null); }} />
              <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0 }}>Test Ended!</h2>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9 }}>Thank you for completing the test. Everything is going to be fine.</p>
            </div>
            <div style={{ overflowY: 'auto', padding: '1.5rem', flex: 1 }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1f2937', margin: '0 0 0.25rem 0' }}>{quiz?.title}</h3>
              {quiz?.description && <p style={{ marginBottom: '1rem', color: '#4b5563' }}>{quiz.description}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', marginBottom: '1.5rem' }}>
                <span><strong>Level:</strong> {quiz?.level}</span>
                {quiz?.division ? <span><strong>Division:</strong> {quiz.division}</span> : null}
                {quiz?.unit ? <span><strong>Unit:</strong> {quiz.unit}</span> : null}
                {quiz?.board ? <span><strong>Board:</strong> {quiz.board}</span> : null}
                {quiz?.year ? <span><strong>Exam Year:</strong> {quiz.year}</span> : null}
                <span><strong>Type:</strong> {quiz?.quiz_type}</span>
                <span><strong>Duration:</strong> {quiz?.duration_minutes} mins</span>
                <span><strong>Start Time:</strong> {quiz?.start_time ? formatBdPst(quiz.start_time) : 'N/A'}</span>
                {(quiz?.quiz_type === 'compitition') && (
                    <span><strong>Ends:</strong> {quiz?.end_time ? formatBdPst(quiz.end_time) : 'N/A'}</span>
                )}
                <span><strong>Created By:</strong> {quiz?.created_by}</span>
                {quiz?.question_ids && (
                    <>
                        {Object.entries(quiz.question_ids).map(([subject, questions]) => (
                            <span key={subject}><strong>{subject}:</strong> {questions.length} questions</span>
                        ))}
                        <span style={{ gridColumn: '1 / -1' }}><strong>Total Questions:</strong> {totalQuestions}</span>
                    </>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '360px', margin: '0 auto' }}>
                {canRetake && (
                    <button onClick={() => (typeof onRetakeQuiz === 'function' ? onRetakeQuiz({ ...quiz }) : (setQuizEnded(false), onQuizEnd(null)))} style={{ width: '100%', backgroundColor: '#f97316', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <PlayCircle style={{ width: '1.25rem', height: '1.25rem' }} /> Retake Test
                    </button>
                )}
                {(() => {
                    // Competition results (details + leaderboard) unlock only after the window closes.
                    const et = quiz?.end_time || quizParam?.end_time;
                    const isComp = (quiz?.quiz_type || quizParam?.quiz_type) === 'compitition';
                    if (isComp && (!et || new Date(et) > new Date())) {
                        return (
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', textAlign: 'center' }}>
                                Your answers are submitted. Results &amp; leaderboard open after the competition ends
                                {et ? <> — <strong>{formatBdPst(et)}</strong></> : null}.
                            </p>
                        );
                    }
                    return (
                        <>
                            <button onClick={() => setShowSubmissionDetails(true)} style={{ width: '100%', backgroundColor: '#3b82f6', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <ClipboardListIcon style={{ width: '1.25rem', height: '1.25rem' }} /> Show Submission Details
                            </button>
                            <button onClick={() => setShowScoreLeaderboard(true)} style={{ width: '100%', backgroundColor: '#6366f1', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <TrophyIcon style={{ width: '1.25rem', height: '1.25rem' }} /> Show Score &amp; Leaderboard
                            </button>
                        </>
                    );
                })()}
              </div>
            </div>

            {showSubmissionDetails && (
                <QuizSubmissionDetailsModal submissionId={quizParam?.submission_id} onClose={() => setShowSubmissionDetails(false)} />
            )}

            {showScoreLeaderboard && (
                <QuizScoreLeaderboardModal quizId={quizId} submissionId={quizParam?.submission_id} onClose={() => setShowScoreLeaderboard(false)} />
            )}
          </div>
        );
    }
    
    if (takeSubmitConfirmation) {
        const questionsYetToAnswer = totalQuestionsCount - answeredQuestionsCount;
        const colorToShow = questionsYetToAnswer === 0 ? 'green' : 'red';
        return (
          <div style={{ textAlign: 'center', padding: '4rem', backgroundColor: '#ffffff', borderRadius: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>Test Ending!?</h2>
            <p style={{ fontSize: '1.125rem', color: '#4b5563', marginBottom: '2rem' }}>Thank you for participating in the test.</p>
            <p style={{ fontSize: '1.125rem', color: '#4b5563', marginBottom: '2rem' }}>There are <span style={{ color: colorToShow }}>{questionsYetToAnswer}</span> questions yet to answer.</p>
            <p style={{ fontSize: '1.125rem', color: '#4b5563', marginBottom: '2rem' }}>Are you sure you want to submit?</p>
            <button onClick={handleSubmitQuiz} style={{ backgroundColor: '#3b82f6', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600' }}>Yes</button>
            <button className="mx-2" onClick={() => { setTakeSubmitConfirmation(false); setQuizEnded(false); }} style={{ backgroundColor: '#3b82f6', color: '#ffffff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600' }}>No</button>
          </div>
        );
    }

    // Determine the height of the fixed header dynamically if possible, or set a reasonable fixed height
    const fixedHeaderHeight = '100px'; // Approximate height of the fixed header content
    const allSubjects = Object.keys(questionsBySubject);

    // One question card (used both stand-alone and inside a passage two-pane). `includePassage`
    // renders the question's own side_text/audio inline — used only for NON-grouped questions;
    // grouped questions show their shared passage once in the left pane instead.
    const renderQuestionCard = (question, qIndex, includePassage) => {
        const isAnswered = userAnswers.hasOwnProperty(question.question_id);
        return (
            <QuizQuestionCard
                key={question.question_id}
                question={question}
                qIndex={qIndex}
                includePassage={includePassage}
                answer={userAnswers[question.question_id]}
                isAnswered={isAnswered}
                isFavorite={favoriteQuestions[question.question_id]}
                onOptionSelect={handleOptionSelect}
                onMultiToggle={handleMultiOptionToggle}
                onBlankSelect={handleBlankSelect}
                onTextChange={handleTextAnswerChange}
                onOrderChange={handleOrderChange}
                onToggleFavorite={handleToggleFavorite}
            />
        );
    };

    return (
        <div style={{ backgroundColor: '#ffffff', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)', width: '96%', maxWidth: 'min(1500px, 97vw)', margin: 'auto', maxHeight: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <div style={{ position: 'sticky', top: '0', zIndex: 10, backgroundColor: '#ffffff', paddingBottom: '1rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', paddingTop: '1rem' }}>
            <button onClick={() => setTakeSubmitConfirmation(true)} style={{ backgroundColor: '#22c55e', color: '#ffffff', padding: '0.6rem 1.2rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>Submit Test</button>
            <div style={{ textAlign: 'center', flexGrow: 1 }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: '0' }}>{quiz.title}</h2>
              <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0.25rem 0 0 0' }}>Questions Answered: {answeredQuestionsCount} / {totalQuestionsCount}</p>
            </div>
            <div className={timeLeft <= 60 ? 'kp-timer-flash' : ''} style={{ backgroundColor: '#ef4444', color: '#ffffff', padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 'bold', fontSize: '1.25rem' }}>{formatTime(timeLeft)}</div>
          </div>
          <div style={{ flexGrow: 1, overflowY: 'auto', paddingTop: '1rem' }}>
            {Object.keys(questionsBySubject).length > 0 ? (
              Object.keys(questionsBySubject).map(subjectName => {
                const subjectQuestions = questionsBySubject[subjectName];
                const subjectAnsweredCount = subjectQuestions.filter(q => userAnswers.hasOwnProperty(q.question_id)).length;
                return (
                  <div key={subjectName} style={{ marginBottom: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                    <button onClick={() => handleToggleSubjectCollapse(subjectName)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', backgroundColor: '#e0f2fe', color: '#1e40af', fontWeight: 'bold', fontSize: '1.125rem', border: 'none', cursor: 'pointer', borderBottom: expandedSubjects[subjectName] ? '1px solid #bfdbfe' : 'none' }}>
                      {subjectName} ({subjectAnsweredCount} / {subjectQuestions.length} Answered)
                      {expandedSubjects[subjectName] ? <ChevronUp style={{ width: '1.5rem', height: '1.5rem' }} /> : <ChevronDown style={{ width: '1.5rem', height: '1.5rem' }} />}
                    </button>
                    {expandedSubjects[subjectName] && (
                      <div style={{ padding: '1rem' }}>
                        {(() => {
                          // Cluster consecutive questions that share a passage group. Each
                          // group renders as a two-pane block (fixed passage left, scrollable
                          // questions right); standalone questions render single-column as before.
                          const clusters = [];
                          subjectQuestions.forEach((q, idx) => {
                            const prev = idx > 0 ? subjectQuestions[idx - 1] : null;
                            if (q.group_id && prev && prev.group_id === q.group_id) {
                              clusters[clusters.length - 1].items.push([q, idx]);
                            } else {
                              clusters.push({ groupId: q.group_id || null, items: [[q, idx]] });
                            }
                          });
                          return clusters.map((cluster, ci) => {
                            if (!cluster.groupId) {
                              return cluster.items.map(([q, idx]) => renderQuestionCard(q, idx, true));
                            }
                            const [firstQ, firstIdx] = cluster.items[0];
                            const size = cluster.items.length;
                            const start = firstIdx + 1;
                            const end = firstIdx + size;
                            // "Complete the Words" cloze groups render inline (the passage with a
                            // text input at each {{n}} blank), exactly like the published model test —
                            // not the two-pane passage/questions split used for reading/listening MCQs.
                            const isCloze = firstQ.group_layout === 'cloze'
                              || (firstQ.side_text && /\{\{\d+\}\}/.test(firstQ.side_text) && cluster.items.every(([q]) => q.question_type === 'TEXT'));
                            if (isCloze) {
                              const members = cluster.items.map(([q]) => q);
                              return (
                                <div key={`grp-${cluster.groupId}-${ci}`} style={{ marginBottom: '1.25rem', border: '1px solid #e9d5ff', borderRadius: '0.5rem', padding: '1rem', background: '#faf5ff' }}>
                                  <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: '0.6rem' }}>
                                    {start === end
                                      ? `Question ${start} (1 question) is for this passage.`
                                      : `Questions ${start}–${end} (${size} questions) are for this passage.`}
                                  </div>
                                  <ClozePassage members={members} answers={userAnswers} onText={handleTextAnswerChange} />
                                </div>
                              );
                            }
                            return (
                              <ResizableSplit
                                key={`grp-${cluster.groupId}-${ci}`}
                                style={{ marginBottom: '1.25rem', border: '1px solid #e9d5ff', borderRadius: '0.5rem', overflow: 'hidden', maxHeight: '68vh' }}
                                initialLeftPct={42}
                                left={
                                  <div style={{ background: '#faf5ff', padding: '1rem', height: '100%' }}>
                                    <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: '0.6rem' }}>
                                      {start === end
                                        ? `Question ${start} (1 question) is for this passage.`
                                        : `Questions ${start}–${end} (${size} questions) are for this passage.`}
                                    </div>
                                    {firstQ.side_text && (
                                      <HighlightableText
                                        className="kp-read-passage"
                                        text={firstQ.side_text}
                                        highlights={highlightsByPassage[cluster.groupId] || []}
                                        onChange={(next) => setHighlightsByPassage(prev => ({ ...prev, [cluster.groupId]: next }))}
                                      />
                                    )}
                                    {firstQ.audio_url && (
                                      <AudioPlayer src={firstQ.audio_url} />
                                    )}
                                  </div>
                                }
                                right={
                                  <div style={{ padding: '1rem' }}>
                                    {cluster.items.map(([q, idx]) => renderQuestionCard(q, idx, false))}
                                  </div>
                                }
                              />
                            );
                          });
                        })()}
                        {/* Per-subject answered tracker, STUCK to the bottom of this subject block:
                            it sticks to the viewport bottom while this subject is scrolled through,
                            then hands off to the next subject's tracker. Negative margins let it
                            span the full panel width and sit flush at the panel's bottom edge. */}
                        <AnsweredTracker
                          questions={subjectQuestions}
                          isAnswered={(q) => userAnswers.hasOwnProperty(q.question_id)}
                          style={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 5,
                            marginTop: '0.75rem',
                            marginLeft: '-1rem',
                            marginRight: '-1rem',
                            marginBottom: '-1rem',
                            borderBottomLeftRadius: '0.5rem',
                            borderBottomRightRadius: '0.5rem',
                            boxShadow: '0 -2px 6px rgba(0,0,0,0.06)',
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No questions available for this test.</div>
            )}
          </div>
        </div>
    );
};



const DailySummarySection = ({ title, emptyText, items, onViewDetails }) => {
    const list = items || [];
    return (
        <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', backgroundColor: '#ffffff', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>{title}</h3>
            {list.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem' }}>{emptyText}</div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                    {list.map(item => (
                        <div
                            key={item.submission_id}
                            onClick={() => onViewDetails && onViewDetails(item.submission_id)}
                            style={{ padding: '1rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', backgroundColor: '#f9fafb', cursor: 'pointer' }}
                            title="Click to review this attempt"
                        >
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                {item.submitted_at ? formatBdPst(item.submitted_at) : '—'}
                            </div>
                            <div style={{ fontWeight: 700, color: '#1f2937' }}>Level: {item.level}</div>
                            <div style={{ marginTop: '0.5rem' }}>
                                <span style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.25rem 0.6rem', borderRadius: '9999px', backgroundColor: '#d1fae5', color: '#065f46' }}>
                                    Score: {item.score}{item.total ? ` / ${item.total}` : ''}
                                </span>
                            </div>
                            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#3b82f6', fontWeight: 600 }}>
                                Review attempt →
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


const UserQuiz = () => {
    const { userState } = useUser();
    // const { dataState } = useData();
    const [level, setLevel, levelExtra] = useLocalStorageLevel();
    // The level-coupled secondary tag the user picked (division for SSC/HSC, unit for
    // VARSITY-*). Sent as a Previous-Year filter so e.g. VARSITY-DU Unit D shows only D.
    const { division: selectedDivision, unit: selectedUnit } = levelExtra;

    // Allow deep-linking to a specific tab via ?view= (e.g. the homepage streak
    // banner links to the Daily Challenge tab).
    const initialView = (() => {
        try {
            const v = new URLSearchParams(window.location.search).get('view');
            return v && QUIZ_VIEW_TYPES[v] ? QUIZ_VIEW_TYPES[v] : QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ;
        } catch {
            return QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ;
        }
    })();
    const [selectedQuizView, setSelectedQuizView] = useState(initialView);
    // Deep-link (from a notice / notification): ?level= sets the level, and
    // ?quiz=<id>&show=leaderboard opens that quiz's scoreboard directly.
    const [deepLinkLeaderboardQuizId, setDeepLinkLeaderboardQuizId] = useState(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            return p.get('show') === 'leaderboard' && p.get('quiz') ? Number(p.get('quiz')) : null;
        } catch {
            return null;
        }
    });
    // Apply a deep-linked ?level= once on mount.
    useEffect(() => {
        try {
            const lvl = new URLSearchParams(window.location.search).get('level');
            if (lvl && lvl !== level) setLevel(lvl);
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Previous Year Question bank filters (board / year / subject / bcs_term). For BCS the
    // year picker is swapped for a bcs_term dropdown.
    const [pyqFilters, setPyqFilters] = useState({ board: '', year: '', subject: '', bcs_term: '' });
    const [quizzes, setQuizzes] = useState([]);
    // Free-text search over the current tab's quizzes (matches title or description).
    const [quizSearch, setQuizSearch] = useState('');
    // "Find Quiz By" filters for the Micro view: a subject, and a topic (group), or 'All'.
    const [microGroupFilter, setMicroGroupFilter] = useState('All');
    const [microSubjectFilter, setMicroSubjectFilter] = useState('All');
    // BCS term options for the PYQ term dropdown (fetched only at BCS level).
    const [bcsTerms, setBcsTerms] = useState([]);
    // The quizzes shown in the grid after applying the subject filter (micro) + title/description search.
    const filteredQuizzes = useMemo(() => {
        let list = quizzes || [];
        if (selectedQuizView === QUIZ_VIEW_TYPES.MICRO_QUIZ && microSubjectFilter !== 'All') {
            list = list.filter(quiz => (quiz.subject || '') === microSubjectFilter);
        }
        const q = quizSearch.trim().toLowerCase();
        if (!q) return list;
        return list.filter(quiz =>
            `${quiz.title || ''} ${quiz.description || ''}`.toLowerCase().includes(q)
        );
    }, [quizzes, quizSearch, selectedQuizView, microSubjectFilter]);
    // Subjects available in the current Micro list (for the Subject filter). Empty for levels whose
    // micros don't carry a subject (e.g. SAT/GRE), which hides the Subject dropdown.
    const microSubjectOptions = useMemo(
        () => (selectedQuizView === QUIZ_VIEW_TYPES.MICRO_QUIZ
            ? [...new Set((quizzes || []).map(q => q.subject).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)))
            : []),
        [quizzes, selectedQuizView]
    );
    // Topic (group) names available for the Topic filter — scoped to the chosen subject so the topic
    // list narrows to that subject's topics. Derived from the full fetched list (not the search).
    const microGroupOptions = useMemo(
        () => {
            if (selectedQuizView !== QUIZ_VIEW_TYPES.MICRO_QUIZ) return [];
            const base = microSubjectFilter !== 'All'
                ? (quizzes || []).filter(q => (q.subject || '') === microSubjectFilter)
                : quizzes;
            return microGroupNames(base, level);
        },
        [quizzes, level, selectedQuizView, microSubjectFilter]
    );
    // Reset the group filter to "All" when the level or tab changes, or if the chosen group is no
    // longer present in the freshly-fetched list.
    useEffect(() => {
        setMicroGroupFilter(prev => (prev !== 'All' && !microGroupOptions.includes(prev) ? 'All' : prev));
    }, [microGroupOptions]);
    // Reset the subject filter to "All" when the level/tab changes or the chosen subject is gone.
    useEffect(() => {
        setMicroSubjectFilter(prev => (prev !== 'All' && !microSubjectOptions.includes(prev) ? 'All' : prev));
    }, [microSubjectOptions]);
    // Subject options for the PYQ Subject dropdown at board levels (SSC/HSC), auto-populated
    // from the taxonomy for the current level + division.
    const [pyqSubjectOptions, setPyqSubjectOptions] = useState([]);
    const [quizzesSubmissionDetails, setQuizzesSubmissionDetails] = useState({});
    // Start in the loading state so the first paint shows the spinner (not the "no quizzes"
    // empty text) while the initial fetch — which waits on level/email hydration — is pending.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [streak, setStreak] = useState(null);
    const [dailyHistory, setDailyHistory] = useState({ anonymous_quick: [], quick: [] });
    const CallApi = useApiCaller();
    const { showError } = useError();

    // State for managing Add/Edit/Detail/Quiz Taking views
    const [showAddForm, setShowAddForm] = useState(false);
    const [showQuizDetails, setShowQuizDetails] = useState(false);
    const [viewingQuiz, setViewingQuiz] = useState(null);
    const [showQuizTaking, setShowQuizTaking] = useState(false);
    const [takingQuiz, setTakingQuiz] = useState(null);
    // 'test' = timed model-test session; 'practice' = self-paced reveal (micro only).
    const [takingMode, setTakingMode] = useState('test');
    // Submission id whose full per-question review is open (daily quick-quiz cards).
    const [summaryDetailId, setSummaryDetailId] = useState(null);
    const handleViewSummaryDetails = useCallback((submissionId) => {
        setSummaryDetailId(submissionId);
    }, []);


    const fetchQuizzes = useCallback(async () => {
        if (!level) {
            console.log("No level found");
            setQuizzes([]);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // The Daily Challenge page groups three sources: the AI-personalized
            // system daily quizzes (which split into attempted / not-yet-attempted in
            // the grid), anonymously-taken quick summaries, and logged-in quick summaries.
            if (selectedQuizView === QUIZ_VIEW_TYPES.DAILY_CHALLENGE) {
                const res = await CallApi({ url: getDailyHistoryUrl(level), method: 'GET', token: true });
                if (res.success && res.data) {
                    setQuizzes(res.data.system_daily || []);
                    setDailyHistory({
                        anonymous_quick: res.data.anonymous_quick || [],
                        quick: res.data.quick || [],
                    });
                } else {
                    setError("Failed to fetch the daily challenge.");
                    setQuizzes([]);
                    setDailyHistory({ anonymous_quick: [], quick: [] });
                }
                return;
            }
            const userQuizUrl = getCustomQuizGetUrl(selectedQuizView, level, userState?.email, 1, 1000, { ...pyqFilters, division: selectedDivision, unit: selectedUnit });
            console.log("Fetching quizzes from:", userQuizUrl);
            // const userQuizGetResponse = await CallApi({ url: userQuizUrl, method: 'GET', token: true });
            const userQuizGetResponse = await CallApi({
                url: userQuizUrl,
                method: 'GET',
                token: true
            });

            if (userQuizGetResponse.success) {
                const fetchedQuizzes = userQuizGetResponse?.data?.quizes;
                setQuizzes(fetchedQuizzes);
            } else {
                setError("Failed to fetch tests.");
                setQuizzes([]);
            }
        } catch (err) {
            setError("An error occurred while fetching tests.");
            setQuizzes([]);
        } finally {
            setLoading(false);
        }
    }, [selectedQuizView, level, userState?.email, pyqFilters, selectedDivision, selectedUnit]);

    // Board/Subject filters only apply to SSC/HSC; the Year filter doesn't apply to language
    // levels (IELTS/TOEFL/GRE) or BCS. Clear any stale values when the level changes so they
    // aren't sent for levels that don't show them.
    useEffect(() => {
        if (!isBoardLevel(level)) setPyqFilters(f => (f.board || f.subject) ? { ...f, board: '', subject: '' } : f);
        if (isLanguageLevel(level)) setPyqFilters(f => (f.year ? { ...f, year: '' } : f));
    }, [level]);

    // If the current tab is hidden for this level (e.g. My Quiz / PYQ under TOEFL), fall back
    // to Published so the user never lands on an empty/disabled tab.
    useEffect(() => {
        const allowed = visibleQuizViews(level);
        if (level && !allowed.includes(selectedQuizView)) {
            setSelectedQuizView(QUIZ_VIEW_TYPES.PUBLISHED_QUIZ);
        }
    }, [level, selectedQuizView]);

    // Picking a level (from the navbar) drops the user into the Full Published Model Tests. This
    // fires only when the level actually changes after mount (initial mount respects any ?view=
    // deep-link), covering the case where the store is already open when the level is switched.
    const prevLevelRef = useRef(level);
    useEffect(() => {
        if (prevLevelRef.current !== level) {
            prevLevelRef.current = level;
            if (level) setSelectedQuizView(QUIZ_VIEW_TYPES.PUBLISHED_QUIZ);
        }
    }, [level]);

    // Pull the user's streak (current + longest) to surface on the Daily Challenge card.
    const fetchStreak = useCallback(async () => {
        try {
            const response = await CallApi({ url: getStreakUrl(), method: 'GET', token: true });
            if (response.success) {
                setStreak(response.data);
            }
        } catch (err) {
            console.log('Fetching streak error:', err);
        }
    }, []);

    useEffect(() => {
        if (selectedQuizView === QUIZ_VIEW_TYPES.DAILY_CHALLENGE) {
            fetchStreak();
        }
    }, [selectedQuizView, refreshTrigger, fetchStreak]);

    const fetchQuizSubmissionDetails= async () => {
        try {
            const userQuizSubmissionDetailsUrl = getQuizSubmissionDetailsUrl();
            const userQuizSubmissionDetailsPayload = getQuizSubmissionDetailsPayload(quizzes);
            console.log("Fetching submission details from:", userQuizSubmissionDetailsUrl);
            const userQuizSubmissionDetailsResponse = await CallApi({
                url: userQuizSubmissionDetailsUrl,
                method: 'POST',
                payload: userQuizSubmissionDetailsPayload,
                token: true
            });

            if (userQuizSubmissionDetailsResponse.success) {
                const fetchedQuizSubmissionDetails = userQuizSubmissionDetailsResponse?.data;
                console.log(`fetchedQuizSubmissionDetails :: `, fetchedQuizSubmissionDetails);
                const modifiedQuizzesSubmissionDetails = modifyQuizzesSubmissionDetails(fetchedQuizSubmissionDetails);
                // setQuizDetails(fetchedQuiz);
                setQuizzesSubmissionDetails(modifiedQuizzesSubmissionDetails);
            }
        } catch (err) {
            console.log(`Fetching quiz submission details in bulk error :: `, err);
            // setQuizDetails(quiz);
        }
    };

    useEffect(() => {
        fetchQuizSubmissionDetails();
    }, [quizzes, level]);

    useEffect(() => {
        fetchQuizzes();
        // Include userState?.email: it hydrates from the user context slightly after mount, and
        // the quiz list URL depends on it — without this the list stays empty until another
        // reload (the "nothing shown, then it appears on a second reload" bug).
    }, [selectedQuizView, level, refreshTrigger, selectedDivision, selectedUnit, pyqFilters, userState?.email]);

    // Clear the title/description search when the tab or level changes.
    useEffect(() => {
        setQuizSearch('');
    }, [selectedQuizView, level]);

    // Load BCS term options when the BCS level is selected (for the PYQ term dropdown).
    // Clear any stale year/term selection so filters don't leak across levels.
    useEffect(() => {
        let cancelled = false;
        if (level !== BCS_LEVEL) {
            setBcsTerms([]);
            setPyqFilters(f => (f.bcs_term ? { ...f, bcs_term: '' } : f));
            return undefined;
        }
        setPyqFilters(f => (f.year ? { ...f, year: '' } : f));
        (async () => {
            const res = await CallApi({ url: getPublicBcsTermsUrl('BCS'), method: 'GET', token: true });
            if (!cancelled && res.success) {
                const data = res.data;
                setBcsTerms(Array.isArray(data) ? data : (data?.terms || data?.bcs_terms || []));
            }
        })();
        return () => { cancelled = true; };
    }, [level, CallApi]);

    // Load Subject options for the PYQ Subject dropdown at board levels (SSC/HSC), scoped to
    // the current level + division. Clear a stale subject selection when the scope changes.
    useEffect(() => {
        let cancelled = false;
        if (!isBoardLevel(level)) { setPyqSubjectOptions([]); return undefined; }
        // Reset a stale subject when the level/division scope changes.
        setPyqFilters(f => (f.subject ? { ...f, subject: '' } : f));
        (async () => {
            const res = await CallApi({
                url: getPublicSubjectsUrl(level, '', { division: selectedDivision, unit: selectedUnit }),
                method: 'GET',
                token: true,
            });
            if (!cancelled && res.success) setPyqSubjectOptions(res.data?.subjects || []);
        })();
        return () => { cancelled = true; };
    }, [level, selectedDivision, selectedUnit, CallApi]);

    // --- Handlers for QuizGrid actions ---
    const handleAddQuiz = useCallback(() => {
        setShowAddForm(true);
        setShowQuizDetails(false);
        setShowQuizTaking(false);
    }, []);

    const [pendingDeleteQuizId, setPendingDeleteQuizId] = useState(null);
    const [deletingQuiz, setDeletingQuiz] = useState(false);

    const handleDeleteQuiz = useCallback((quizId) => {
        setPendingDeleteQuizId(quizId);
    }, []);

    const cancelDeleteQuiz = useCallback(() => {
        setPendingDeleteQuizId(null);
    }, []);

    const performDeleteQuiz = useCallback(async () => {
        if (pendingDeleteQuizId == null) return;
        setDeletingQuiz(true);
        try {
            const deleteUrl = getCustomQuizDeleteUrl();
            const deletePayload = getCustomQuizDeletePayload([pendingDeleteQuizId]);
            const response = await CallApi({ url: deleteUrl, method: 'DELETE', payload: deletePayload, token: true });
            if (response.success) {
                setRefreshTrigger(t => t + 1); // refresh via the live effect (avoids the stale fetchQuizzes closure)
            } else {
                alert(`Failed to delete test: ${response.message || 'Unknown error'}`);
            }
        } catch (error) {
            alert("An error occurred during deletion.");
            console.error("Delete Quiz Error:", error);
        } finally {
            setDeletingQuiz(false);
            setPendingDeleteQuizId(null);
        }
    }, [pendingDeleteQuizId]);

    const handleViewQuizDetails = useCallback((quiz) => {
        setViewingQuiz(quiz);
        setShowQuizDetails(true);
        setShowAddForm(false);
        setShowQuizTaking(false);
    }, []);

    const handleStartQuiz = useCallback(async (quiz, isResume = false) => {
        setTakingMode('test');
        if (isResume) {
            setTakingQuiz(quiz);
            setShowQuizTaking(true);
            setShowQuizDetails(false);
            setShowAddForm(false);
            return;
        }
        try {
            const quizsubmissionStartUrl = getQuizSubmissionStartUrl();
            // Competition attempts are server-clocked (start=now, end=now+2h) and take-once, so
            // do NOT send the quiz's shared window — let the backend own the per-user 2h clock.
            const startTime = null;
            const endTime = null;
            const quizsubmissionStartPayload = constructQuizSubmissionStartPayload(quiz.quiz_id, 1, quiz.quiz_type, quiz.duration_minutes, startTime, endTime);
            const response = await CallApi({ url: quizsubmissionStartUrl, method: 'POST', payload: quizsubmissionStartPayload, token: true });
            console.log(`Quiz submission start response: ${response}`);
            if (response.success) {
                const submissionId = response.data.submission_id;
                const quizStartTimeFromResponse = response.data.start_time;
                const quizEndTimeFromResponse = response.data.end_time;
                quiz["submission_id"] = submissionId;
                quiz["start_time"] = quizStartTimeFromResponse;
                quiz["end_time"] = quizEndTimeFromResponse;
                setTakingQuiz(quiz);
                setShowQuizTaking(true);
                setShowQuizDetails(false);
                setShowAddForm(false);
            } else {
                // alert(`Failed to delete quiz: ${response.message || 'Unknown error'}`);
                setTakingQuiz(quiz);
                setShowQuizTaking(false);
                setShowQuizDetails(true);
                setShowAddForm(false);
            }
        } catch (error) {
            // alert("An error occurred during deletion.");
            console.error("Delete Quiz Error:", error);
            setTakingQuiz(quiz);
            setShowQuizTaking(false);
            setShowQuizDetails(true);
            setShowAddForm(false);
        }

    }, []);

    // Self-paced practice: no submission, no timer — just reveal answers per question.
    const handleStartPractice = useCallback((quiz) => {
        setTakingMode('practice');
        setTakingQuiz(quiz);
        setShowQuizTaking(true);
        setShowQuizDetails(false);
        setShowAddForm(false);
    }, []);

    const handlePracticeClose = useCallback(() => {
        setShowQuizTaking(false);
        setTakingQuiz(null);
        setTakingMode('test');
    }, []);

    const handleQuizFormSave = useCallback(() => {
        setShowAddForm(false);
        // Bump refreshTrigger instead of calling fetchQuizzes() directly: this useCallback has []
        // deps, so a direct call would run the STALE fetchQuizzes captured at first render (wrong
        // tab / empty email) and the new quiz wouldn't appear until a reload. The refresh effect
        // (deps include refreshTrigger) re-runs the CURRENT fetchQuizzes with the live tab/level.
        setRefreshTrigger(t => t + 1);
    }, []);

    const handleQuizFormCancel = useCallback(() => {
        setShowAddForm(false);
    }, []);

    const handleQuizEnd = useCallback(async (quiz) => {
        if (!quiz) {
            setShowQuizTaking(false);
            setTakingQuiz(null);
            return Promise.resolve();
        }
        try {
            const quizsubmissionEndUrl = getQuizSubmissionEndUrl();
            const submissionId = quiz.submission_id;
            const userSubmittedAnswers = quiz.submitted_answers;
            const quizsubmissionEndPayload = constructQuizSubmissionEndPayload(submissionId, userSubmittedAnswers);
            // silent: this call handles its own errors below — a benign "already finalized" (the
            // server's overdue sweep beat this client submit; the attempt is already scored) must
            // NOT paint a red toast. Genuine failures are surfaced explicitly.
            const response = await CallApi({ url: quizsubmissionEndUrl, method: 'POST', payload: quizsubmissionEndPayload, token: true, silent: true });
            console.log(`Quiz submission end response: ${response}`);
            const alreadyFinalized = !response.success && response.status === 400
                && /already finalized/i.test(response.message || '');
            if (response.success || alreadyFinalized) {
                console.log(alreadyFinalized ? "Quiz already finalized (server backstop) — treating as submitted" : "Success in submitting the quiz at the end");
                // Re-drive the live fetch effects with the CURRENT tab/level rather
                // than the stale closures handleQuizEnd captured at first render.
                // fetchQuizzes re-runs -> setQuizzes -> the [quizzes, level] effect
                // re-runs fetchQuizSubmissionDetails with fresh data.
                setRefreshTrigger(t => t + 1);
            } else {
                console.log("Error submitting the quiz at the end");
                showError(response.message || 'There was a problem submitting your test.');
            }
            // setShowQuizTaking(false);
            // setTakingQuiz(null);
            return Promise.resolve();
        } catch (error) {
            // alert("An error occurred during deletion.");
            console.error("Error submitting the quiz at the end with error:", error);
            return Promise.reject(error);
        }
        // setShowQuizTaking(false);
        // setTakingQuiz(null);
    }, [CallApi, showError]);

    return (
        <div style={{ padding: '2rem', minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#7c3aed', marginBottom: '2rem', textAlign: 'center' }}>
            Your {level ? `${level} ` : ''}Model Tests
          </h1>
      
          {/* The quiz list always renders; any active modal overlays it (dimmed,
              still visible) so the "Your Quizzes" page stays in view. */}
          <>
              {(!level || level === '') && (
                <>
                  <div style={{ padding: '1rem', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: '0.5rem', textAlign: 'center', marginBottom: '2rem' }}>
                    Please select a level from <span style={{ color: 'green' }}>Homepage</span> or <span style={{ color: 'green' }}>Set Level Dropdown</span> from Navigation Bar to view the tests.
                  </div>
                </>
              )}

              {level && level !== '' && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '2rem', padding: '0.5rem', backgroundColor: '#ffffff', borderRadius: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)' }}>
                  {Object.values(QUIZ_VIEW_TYPES).filter(v => visibleQuizViews(level).includes(v)).map(viewType => (
                    <button
                      key={viewType}
                      onClick={() => setSelectedQuizView(viewType)}
                      className={`kp-quizview-btn${selectedQuizView === viewType ? ' active' : ''}`}
                    >
                      {quizViewLabel(viewType, level)}
                    </button>
                  ))}
                </div>
              )}

              {level && (
                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {selectedQuizView === QUIZ_VIEW_TYPES.DAILY_CHALLENGE && streak && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '0.75rem', color: '#9a3412', fontWeight: 600 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Flame style={{ width: '1.5rem', height: '1.5rem' }} fill="#f97316" color="#f97316" />
                        {streak.current_streak_days}-day streak
                      </span>
                      <span style={{ color: '#92400e' }}>Longest: {streak.longest_streak_days} days</span>
                    </div>
                  )}
                  {selectedQuizView === QUIZ_VIEW_TYPES.DAILY_CHALLENGE && (
                    <div style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#2563eb', fontWeight: 600 }}>
                      💡 You can click on the Kobraprep logo and take the &lsquo;Randomly Selected Quick Tests&rsquo; many times.
                    </div>
                  )}
                  {loading && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#4b5563' }}>
                      <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                        <span className="visually-hidden">Loading…</span>
                      </div>
                      <div className="mt-2">Loading tests…</div>
                    </div>
                  )}
                  {error && <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>Error: {error}</div>}
                  {!loading && !error && selectedQuizView === QUIZ_VIEW_TYPES.DAILY_CHALLENGE && (
                    <>
                      <div className="kp-filter d-flex flex-wrap gap-2 mb-3 align-items-end">
                        <div>
                          <label>Search</label>
                          <input type="search" className="form-control form-control-sm" placeholder="Title or description…" value={quizSearch} onChange={e => setQuizSearch(e.target.value)} style={{ minWidth: '220px' }} />
                        </div>
                        {quizSearch && (
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setQuizSearch('')}>Clear</button>
                        )}
                      </div>
                      <QuizGrid
                        quizzes={filteredQuizzes}
                        quizzesSubmissionDetails={quizzesSubmissionDetails}
                        title="AI Assisted Personalized Daily Tests"
                        onEdit={null}
                        onDelete={null}
                        onViewDetails={handleViewQuizDetails}
                        onAddQuiz={null}
                        quizViewType={selectedQuizView}
                      />
                      <DailySummarySection
                        title="Daily Quick Tests (Random Question & Without Login)"
                        emptyText="No anonymously-taken tests. Quick tests you took before logging in show up here."
                        items={dailyHistory.anonymous_quick}
                        onViewDetails={handleViewSummaryDetails}
                      />
                      <DailySummarySection
                        title="Daily Quick Tests (Random Question & Logged in)"
                        emptyText="No quick tests yet. Take a Daily Quick Test from the homepage."
                        items={dailyHistory.quick}
                        onViewDetails={handleViewSummaryDetails}
                      />
                    </>
                  )}
                  {!loading && !error && selectedQuizView !== QUIZ_VIEW_TYPES.DAILY_CHALLENGE && (
                    <>
                    {selectedQuizView === QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ && (!userState?.currentPlan || userState.currentPlan === 'FREE') && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.5rem', color: '#92400e' }}>
                        <span>Free plan shows only the most recent exam years. Upgrade to unlock the full previous-year bank.</span>
                        <UpgradeNudge message="Upgrade to unlock years" />
                      </div>
                    )}
                    <div className="kp-filter d-flex flex-wrap gap-2 mb-3 align-items-end">
                      {selectedQuizView === QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ && (
                        <>
                          {isBoardLevel(level) && (
                            <div>
                              <label>Board</label>
                              <select className="form-select form-select-sm" value={pyqFilters.board} onChange={e => setPyqFilters(f => ({ ...f, board: e.target.value }))}>
                                <option value="">All boards</option>
                                {BOARD_VALUES.map(b => <option key={b} value={b}>{b}</option>)}
                              </select>
                            </div>
                          )}
                          {level === BCS_LEVEL ? (
                            <div>
                              <label>BCS Term</label>
                              <select className="form-select form-select-sm" value={pyqFilters.bcs_term} onChange={e => setPyqFilters(f => ({ ...f, bcs_term: e.target.value }))}>
                                <option value="">All BCS terms</option>
                                {sortBcsTerms(bcsTerms.map(t => (typeof t === 'string' ? t : (t.name || t.bcs_term || t.term || '')))).filter(Boolean).map(term => (
                                  <option key={term} value={term}>{term}</option>
                                ))}
                              </select>
                            </div>
                          ) : isLanguageLevel(level) ? null : (
                            <div>
                              <label>Year</label>
                              <div>
                                <YearPicker
                                  value={pyqFilters.year}
                                  onChange={y => setPyqFilters(f => ({ ...f, year: y }))}
                                  placeholder="All years"
                                  className="form-control form-control-sm"
                                />
                              </div>
                            </div>
                          )}
                          {isBoardLevel(level) && (
                            <div>
                              <label>Subject</label>
                              <select className="form-select form-select-sm" value={pyqFilters.subject} onChange={e => setPyqFilters(f => ({ ...f, subject: e.target.value }))}>
                                <option value="">All subjects</option>
                                {pyqSubjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                          )}
                        </>
                      )}
                      <div>
                        <label>Search</label>
                        <input type="search" className="form-control form-control-sm" placeholder="Title or description…" value={quizSearch} onChange={e => setQuizSearch(e.target.value)} style={{ minWidth: '220px' }} />
                      </div>
                      {selectedQuizView === QUIZ_VIEW_TYPES.MICRO_QUIZ && microSubjectOptions.length > 0 && (
                        <div>
                          <label>Subject :</label>
                          <select className="form-select form-select-sm" value={microSubjectFilter} onChange={e => { setMicroSubjectFilter(e.target.value); setMicroGroupFilter('All'); }} style={{ minWidth: '180px' }}>
                            <option value="All">All subjects</option>
                            {microSubjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      )}
                      {selectedQuizView === QUIZ_VIEW_TYPES.MICRO_QUIZ && microGroupOptions.length > 0 && (
                        <div>
                          <label>Topic :</label>
                          <select className="form-select form-select-sm" value={microGroupFilter} onChange={e => setMicroGroupFilter(e.target.value)} style={{ minWidth: '200px' }}>
                            <option value="All">All topics</option>
                            {microGroupOptions.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                      )}
                      {(pyqFilters.board || pyqFilters.year || pyqFilters.subject || pyqFilters.bcs_term || quizSearch || (microGroupFilter && microGroupFilter !== 'All') || (microSubjectFilter && microSubjectFilter !== 'All')) && (
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { setPyqFilters({ board: '', year: '', subject: '', bcs_term: '' }); setQuizSearch(''); setMicroGroupFilter('All'); setMicroSubjectFilter('All'); }}>Clear</button>
                      )}
                    </div>
                    <QuizGrid
                      quizzes={filteredQuizzes}
                      quizzesSubmissionDetails={quizzesSubmissionDetails}
                      title={quizViewLabel(selectedQuizView, level)}
                      onEdit={null}
                      onDelete={selectedQuizView === QUIZ_VIEW_TYPES.MY_QUIZ ? handleDeleteQuiz : null}
                      onViewDetails={handleViewQuizDetails}
                      onAddQuiz={handleAddQuiz}
                      quizViewType={selectedQuizView}
                      level={level}
                      microGroupFilter={microGroupFilter}
                    />
                    </>
                  )}
                </div>
              )}
          </>

          {(showAddForm || showQuizDetails || showQuizTaking) && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '74px', zIndex: 999, backdropFilter: 'blur(1px)' }}>
              {showAddForm && <QuizAddUserForm onCancel={handleQuizFormCancel} onSave={handleQuizFormSave} />}
              {showQuizDetails && <QuizDetailModal quizSubmissionDetails={quizzesSubmissionDetails[viewingQuiz.quiz_id]} quiz={viewingQuiz} onClose={() => setShowQuizDetails(false)} onStartQuiz={handleStartQuiz} onStartPractice={handleStartPractice} />}
              {showQuizTaking && (takingMode === 'practice'
                ? <QuizPracticeInterface key={`practice-${takingQuiz?.quiz_id}`} quiz={takingQuiz} onClose={handlePracticeClose} />
                : takingQuiz?.metadata?.test_kind === 'toefl_model_test'
                ? <ToeflModelTest key={takingQuiz?.submission_id} quizParam={takingQuiz} onQuizEnd={handleQuizEnd} />
                : takingQuiz?.metadata?.test_kind === 'ielts_model_test'
                ? <IeltsModelTest key={takingQuiz?.submission_id} quizParam={takingQuiz} onQuizEnd={handleQuizEnd} />
                : takingQuiz?.metadata?.test_kind === 'gre_model_test'
                ? <GreModelTest key={takingQuiz?.submission_id} quizParam={takingQuiz} onQuizEnd={handleQuizEnd} />
                : takingQuiz?.metadata?.test_kind === 'sat_model_test'
                ? <SatModelTest key={takingQuiz?.submission_id} quizParam={takingQuiz} onQuizEnd={handleQuizEnd} />
                : <QuizTakingInterface key={takingQuiz?.submission_id} quizParam={takingQuiz} onQuizEnd={handleQuizEnd} onRetakeQuiz={handleStartQuiz} />)}
            </div>
          )}
          <ConfirmDeleteDialog
            open={pendingDeleteQuizId != null}
            title="Delete test"
            message="Are you sure you want to delete this test? This cannot be undone."
            onConfirm={performDeleteQuiz}
            onCancel={cancelDeleteQuiz}
            busy={deletingQuiz}
          />
          {summaryDetailId != null && (
            <QuizSubmissionDetailsModal
              submissionId={summaryDetailId}
              onClose={() => setSummaryDetailId(null)}
            />
          )}
          {/* Deep-linked competition scoreboard (from a notice / result notification). */}
          {deepLinkLeaderboardQuizId != null && (
            <QuizScoreLeaderboardModal
              quizId={deepLinkLeaderboardQuizId}
              onClose={() => setDeepLinkLeaderboardQuizId(null)}
            />
          )}
        </div>
    );
};

export default UserQuiz;
